/** Centralized error model. Every failed request becomes an ApiError, so UI
 *  error states render one consistent shape regardless of where it failed. */

export type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status?: number;
  readonly details?: unknown;

  constructor(kind: ApiErrorKind, message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.details = details;
  }
}

/** Map an HTTP status + payload to a user-facing ApiError. */
export function mapHttpError(status: number, body: unknown): ApiError {
  const message =
    (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
      ? body.message
      : undefined) ?? defaultMessage(status);

  const details = body && typeof body === 'object' && 'details' in body ? body.details : undefined;

  if (status === 401) return new ApiError('unauthorized', message, status, details);
  if (status === 403) return new ApiError('forbidden', message, status, details);
  if (status === 404) return new ApiError('notFound', message, status, details);
  if (status === 422 || status === 400) return new ApiError('validation', message, status, details);
  if (status >= 500) return new ApiError('server', message, status, details);
  return new ApiError('unknown', message, status, details);
}

function defaultMessage(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You don’t have access to this resource.';
  if (status === 404) return 'The requested data could not be found.';
  if (status >= 500) return 'The analytics service is having trouble. Please retry.';
  return `Request failed (${status}).`;
}
